import { MONITORED_FEATURE_CODES } from './plan-feature-codes';

export type FeatureStatus = 'active' | 'pending active' | 'failed';

export type RawFeature = {
  code: string;
  status: FeatureStatus;
  nextRetryDate: string | null;
};

export type ActiveFeatureItem = {
  rowKey: string;
  title: string;
};

export type NonActiveFeatureItem = {
  rowKey: string;
  title: string;
  retry: string | null;
};

export type GroupedFeatures = {
  active: ActiveFeatureItem[];
  pending: NonActiveFeatureItem[];
  failed: NonActiveFeatureItem[];
};

// Display names for each feature code.
// Codes that map to multiple names (EFXVS, EXPAA/LNAA, FTM) return two items —
// each becomes a separate row in the pending/failed lists, matching Angular behaviour.
// TODO: confirm exact label strings with product team when CMS translations are available.
const CODE_DISPLAY_NAMES: Record<string, string[]> = {
  ALTLOANXPN: ['Alternative Loan Monitoring'],
  BAMA: ['Bank Account Monitoring'],
  EFXCM: ['Equifax Credit Monitoring'],
  EFXCMT: ['Equifax Credit Monitoring'],
  NCOA: ['Change of Address Monitoring'],
  EFXVS: ['Equifax VantageScore', 'Equifax Credit Report'],
  EXPAA: ['Experian Credit Monitoring', 'Experian Credit Score'],
  LNAA: ['Experian Credit Monitoring', 'Experian Credit Score'],
  EXPSIM: ['Experian Score Index Model'],
  FINCALC: ['Financial Calculator'],
  FOURIQ: ['4IQ Dark Web Monitoring'],
  FTM: ['Financial Transaction Monitoring', 'Experian Boost'],
  HTMON: ['Home Title Monitoring'],
  PRP: ['Home Title Monitoring'],
  PRM: ['Home Title Monitoring'],
  INSURANCE: ['Identity Theft Insurance'],
  PRS: ['Single Bureau Credit Score'],
  PRC: ['Credit Report Card'],
  SECURITY_FREEZE: ['Security Freeze'],
  PRI: ['Identity Restoration'],
  SOCIAL_MONITOR: ['Social Media Monitoring'],
  SPENDING_TRACKER: ['Spending Tracker'],
  TUCM: ['TransUnion Credit Monitoring'],
  TUCMT: ['TransUnion Credit Monitoring'],
  TUNIQ: ['TransUnion Credit Monitoring'],
  TUNIQB: ['TransUnion Credit Monitoring'],
  TUCMB: ['TransUnion Credit Monitoring'],
  WALLET_PROTECTION: ['Lost Wallet Protection'],
  XPNCL: ['Experian Credit Lock'],
  EXPCMB: ['Experian Credit Monitoring'],
  EXPCM: ['Experian Credit Monitoring'],
  NEWS_ARTICLES: ['News & Articles'],
  PASSWORD_MANAGER_SP: ['Password Manager'],
  EXPRTA: ['Experian Rental Trade-line'],
  EXPDA: ['Experian Deceased Alert'],
  EXPPA: ['Experian Phone Monitoring'],
  EXPCA: ['Experian Credit Alerts'],
};

export function applyCodeAliases(code: string): string[] {
  return CODE_DISPLAY_NAMES[code] ?? [code];
}

export function formatRetryDate(isoDate: string | null): string | null {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return null;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
}

// When both LNAA and EXPAA are present, keep the one with the worse status.
// Tie-break: prefer LNAA (open question Q3 safe default — see ADR).
export function deduplicateLnaaExpaa(features: RawFeature[]): RawFeature[] {
  const STATUS_PRIORITY: Partial<Record<FeatureStatus, number>> = {
    failed: 0,
    'pending active': 1,
    active: 2,
  };

  const exceptional = features
    .filter((f) => f.code === 'LNAA' || f.code === 'EXPAA')
    .sort((a, b) => {
      const pA = STATUS_PRIORITY[a.status] ?? 3;
      const pB = STATUS_PRIORITY[b.status] ?? 3;
      if (pA !== pB) return pA - pB;
      return a.code === 'LNAA' ? -1 : 1;
    });

  if (exceptional.length <= 1) return features;
  const codeToRemove = exceptional[1].code;
  return features.filter((f) => f.code !== codeToRemove);
}

export function hasPendingOrFailedFeatures(features: RawFeature[]): boolean {
  if (!features?.length) return false;
  return features.some(
    (f) =>
      (f.status === 'pending active' || f.status === 'failed') &&
      (MONITORED_FEATURE_CODES as readonly string[]).includes(f.code),
  );
}

export function groupByStatus(features: RawFeature[]): GroupedFeatures {
  const result: GroupedFeatures = { active: [], pending: [], failed: [] };

  const deduplicated = deduplicateLnaaExpaa(features);
  const monitored = (code: string) =>
    (MONITORED_FEATURE_CODES as readonly string[]).includes(code);

  deduplicated
    .filter((feature) => monitored(feature.code))
    .forEach((feature) => {
      const titles = applyCodeAliases(feature.code);
      const retry = formatRetryDate(feature.nextRetryDate);

      if (feature.status === 'active') {
        titles.forEach((title, i) =>
          result.active.push({
            rowKey: `${feature.code}-a${i}`,
            title,
          }),
        );
      } else if (feature.status === 'pending active') {
        titles.forEach((title, i) =>
          result.pending.push({
            rowKey: `${feature.code}-p${i}`,
            title,
            retry,
          }),
        );
      } else if (feature.status === 'failed') {
        titles.forEach((title, i) =>
          result.failed.push({
            rowKey: `${feature.code}-f${i}`,
            title,
            retry,
          }),
        );
      }
    });

  result.active.sort((a, b) =>
    a.title.toLowerCase().localeCompare(b.title.toLowerCase()),
  );
  result.pending.sort((a, b) =>
    a.title.toLowerCase().localeCompare(b.title.toLowerCase()),
  );
  result.failed.sort((a, b) =>
    a.title.toLowerCase().localeCompare(b.title.toLowerCase()),
  );

  return result;
}
